import { useSuspenseQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { cn, queries, useAuthConfig } from "@theprelude/core";
import { Button, ServerIcon } from "@theprelude/ds";
import { ReactNode } from "react";
import { missingMFAOData } from "@/components/filter-form/additional-filters";
import { getOdataFilters } from "@/components/filter-form/odata";
import { Filter } from "@/lib/filter";
import { tb } from "@/lib/tracking";
import { useExceptedFilters } from "@/queries/control-monitor";
import { Card, CardHeader, getNavigationOptions, Metric } from "../sections";
import { SummaryStatusIcon } from "../summary-icon-bubbles";

export const MissingMFACard = () => {
  const filters = useMissingMFAFilters();
  const { showExcepted } = useExceptedFilters();

  const authConfig = useAuthConfig();
  const { data: summary } = useSuspenseQuery(
    queries.scm.summary(authConfig, {
      usersQuery: {
        filter: { and: getOdataFilters(filters) },
      },
    }),
  );
  const missingMFACount = showExcepted
    ? summary.user_summary.user_count
    : summary.user_summary.excepted.user_count;

  return (
    <UsersSummaryCard
      title="Missing MFA"
      count={missingMFACount}
      filters={filters}
    >
      Users without multi-factor authentication enabled.
    </UsersSummaryCard>
  );
};

const useMissingMFAFilters = (): Filter[] => [
  { ...missingMFAOData, operator: "is", value: true },
];

const UsersSummaryCard = ({
  title,
  children,
  count,
  filters,
}: {
  title: ReactNode;
  children: ReactNode;
  count: number;
  filters: Filter[];
}) => (
  <Card className="flex gap-4">
    <div className="flex flex-1 flex-col gap-4">
      <CardHeader>
        <SummaryStatusIcon success={count === 0} />
        {title}
      </CardHeader>
      <div className="text-base">{children}</div>
      <div>
        <Link {...getNavigationOptions({ overlay: ["users", { filters }] })}>
          <Button
            intent="secondary"
            icon={<ServerIcon className="text-secondary-foreground" />}
            className={cn("inline-flex", tb(title))}
          >
            View users
          </Button>
        </Link>
      </div>
    </div>
    <Metric label="Users" count={count} />
  </Card>
);
